import React, { useState, useEffect } from 'react';
import {
    Box,
    Title,
    Text,
    TextInput,
    Button,
    Alert,
    Paper,
    Select,
    Stack,
    Group,
} from '@mantine/core';
import { IconLock } from '@tabler/icons-react';
import { getAxiosInstance } from '../../services/axiosInstance';
import { getScimConfig } from '../../utils/envUtils';
import { entityService } from '../../services/entityServiceSingleton';

const CREDENTIAL_ENTITY_TYPE = 'Credential';
const DEFAULT_ATTRIBUTE = '$.password';

interface CredentialItem {
    Id: string;
    DisplayName?: string;
    [key: string]: any;
}

const EncDec: React.FC = () => {
    const [credentials, setCredentials] = useState<CredentialItem[]>([]);
    const [credentialsLoading, setCredentialsLoading] = useState(true);
    const [credentialsError, setCredentialsError] = useState<string | null>(null);
    const [selectedCredentialId, setSelectedCredentialId] = useState<string>('');
    const [targetId, setTargetId] = useState('');
    const [attribute, setAttribute] = useState(DEFAULT_ATTRIBUTE);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [result, setResult] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        setCredentialsLoading(true);
        setCredentialsError(null);
        entityService
            .getEntitiesByType(CREDENTIAL_ENTITY_TYPE, 1, 500, undefined, undefined, ['Id', 'DisplayName'])
            .then((res) => {
                if (!cancelled && res?.data) {
                    setCredentials(Array.isArray(res.data) ? res.data : []);
                }
            })
            .catch((err) => {
                if (!cancelled) {
                    setCredentialsError(err?.message ?? 'Failed to load Credentials');
                    setCredentials([]);
                }
            })
            .finally(() => {
                if (!cancelled) setCredentialsLoading(false);
            });
        return () => { cancelled = true; };
    }, [entityService]);

    const handleDecrypt = async () => {
        const tid = targetId.trim();
        if (!tid) {
            setError('Target ID is required.');
            setResult(null);
            return;
        }
        setError(null);
        setResult(null);
        setLoading(true);
        try {
            const api = getAxiosInstance(getScimConfig().apiUrl);
            const pathAttribute = attribute.trim() || DEFAULT_ATTRIBUTE;
            const path = `mgmt/encdec/${encodeURIComponent(tid)}/${pathAttribute}`;
            const response = await api.get(path);
            const data = response.data;
            if (data !== undefined && data !== null) {
                setResult(typeof data === 'string' ? data : JSON.stringify(data, null, 2));
            } else {
                setResult('');
            }
        } catch (err: any) {
            const message = err?.response?.data?.message ?? err?.response?.data ?? err?.message ?? 'Request failed';
            setError(typeof message === 'object' ? JSON.stringify(message) : String(message));
            setResult(null);
        } finally {
            setLoading(false);
        }
    };

    const credentialData = [
        { value: '', label: '— None / enter ID manually —' },
        ...credentials.map((c) => ({
            value: c.Id,
            label: c.DisplayName ?? c.Id,
        })),
    ];

    return (
        <Box p="lg">
            <Group gap="sm" mb="xs">
                <IconLock size={20} />
                <Title order={5}>EncDec – Decrypt attribute value</Title>
            </Group>
            <Text size="sm" c="dimmed" mb="md">
                Calls GET /api/scim/v2/mgmt/encdec/&#123;TargetId&#125;/&#123;Attribute&#125; to decrypt the stored value for the given target and attribute path.
            </Text>
            <Paper withBorder p="md" maw={560}>
                <Stack gap="md">
                    <Select
                        label="Credential"
                        placeholder="Select credential"
                        data={credentialData}
                        value={selectedCredentialId}
                        onChange={(v) => {
                            setSelectedCredentialId(v ?? '');
                            setTargetId(v || '');
                        }}
                        disabled={credentialsLoading}
                        size="sm"
                    />
                    {credentialsError && (
                        <Text size="xs" c="red">
                            {credentialsError}
                        </Text>
                    )}
                    <TextInput
                        label="Target ID"
                        value={targetId}
                        onChange={(e) => {
                            const v = e.currentTarget.value;
                            setTargetId(v);
                            if (!v) setSelectedCredentialId('');
                            else if (selectedCredentialId && v !== selectedCredentialId) setSelectedCredentialId('');
                        }}
                        placeholder="e.g. select a Credential above or enter ID"
                        size="sm"
                    />
                    <TextInput
                        label="Attribute"
                        value={attribute}
                        onChange={(e) => setAttribute(e.currentTarget.value)}
                        placeholder={DEFAULT_ATTRIBUTE}
                        description="Default is $.password; value is sent as the path segment (URL-encoded)."
                        size="sm"
                    />
                    <Button
                        onClick={handleDecrypt}
                        loading={loading}
                        disabled={!targetId.trim()}
                        mt="md"
                    >
                        Decrypt
                    </Button>
                </Stack>
            </Paper>
            {error && (
                <Alert color="red" mt="md" withCloseButton onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}
            {result !== null && !error && (
                <Paper withBorder p="md" mt="md">
                    <Text size="sm" fw={600} c="dimmed" mb="xs">
                        Decrypted value
                    </Text>
                    <Text component="pre" ff="monospace" size="sm" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                        {result || '(empty)'}
                    </Text>
                </Paper>
            )}
        </Box>
    );
};

export default EncDec;
